<script setup>
import { storeToRefs } from "pinia";
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";

import { RIHLogo, useUserStore, useIntegrationsStore } from "@/entities";
import {
    BaseHeaderTemplate,
    BaseButton,
    BaseDropdown,
    IconBars,
    IconLifebuoy,
    // IconBell,
    IconArrowRightFromBracket,
    RENT_ROUTE_NAMES,
    REPAIR_ROUTE_NAMES,
    vTooltip,
    SALARY_ROUTE_NAMES,
    IconComments,
} from "@/shared";

import TheHeaderExitModal from "./TheHeaderExitModal.vue";
const { t } = useI18n();

defineProps({
    isSidebarCollapsed: {
        type: Boolean,
        default: false,
    },
});

defineEmits(["changeSidebarMode"]);

const userStore = useUserStore();
const { integrilla } = storeToRefs(useIntegrationsStore());

const fastCreateLinks = computed(() => {
    const res = [];
    const rentRights = userStore.rights?.RENT;
    const stockRights = userStore.rights?.STOCK;
    const repairRights = userStore.rights?.REPAIR;

    rentRights?.canAdd &&
        res.push({
            title: t("orders.create_rental"),
            link: { name: RENT_ROUTE_NAMES.ADD },
        });

    repairRights?.canAdd &&
        res.push({
            title: t("orders.create_request"),
            link: { name: REPAIR_ROUTE_NAMES.ORDER.ADD },
        });

    stockRights?.canAdd &&
        res.push({
            title: t("orders.process_sale"),
            link: { name: SALARY_ROUTE_NAMES.ADD },
        });

    return res;
});

const HELP_LINKS = [
    {
        title: t("words.directory"),
        link: { name: "RIH_HELP" },
        const: "DOCS",
    },
    {
        title: t("words.support"),
        link: { name: "RIH_JIVO" },
        const: "SUPPORT",
    },
    {
        title: t("words.whats_new"),
        link: { name: "RIH_CHANGELOG" },
        const: "CHANGELOG",
    },
    {
        title: t("words.training"),
        link: { name: "RIH_INTRO" },
        const: "INTRO",
    },
];

const exitModal = ref(null);
</script>

<template>
    <BaseHeaderTemplate
        class="the-header"
        :is-sidebar-collapsed="isSidebarCollapsed"
    >
        <template #logo>
            <RIHLogo :is-collapsed="isSidebarCollapsed" />
        </template>
        <template #content>
            <BaseButton
                class="the-header__collapse-btn the-header__btn"
                variant="white"
                type="square"
                @click="$emit('changeSidebarMode')"
            >
                <IconBars />
            </BaseButton>
            <BaseDropdown
                v-if="fastCreateLinks.length > 0"
                class="the-header__fast-create-btn the-header__btn"
                button-variant="secondary"
                button-size="sm"
                button-type="rounded"
                :links="fastCreateLinks"
            >
                <template #button-content> {{ $t("orders.create") }} </template>
            </BaseDropdown>
            <!-- Профиль теперь в футере сайдбара (TheSidebar) — кнопку из шапки убрали -->
            <BaseButton
                v-if="integrilla.chat && integrilla.token"
                id="integrilla-button"
                class="the-header__btn"
                variant="primary"
                type="circle"
                v-tooltip.bottom="'Integrilla'"
                @click="$emit('toggleChat')"
            >
                <IconComments class="the-header__icon" />
            </BaseButton>
            <BaseDropdown
                ref="menuDropdown"
                button-variant="invisible"
                button-type="circle"
                button-class="the-header__btn the-header__btn--ghost"
                :links="HELP_LINKS"
                :tooltip-text="$t('words.support_help')"
            >
                <template #button-content>
                    <IconLifebuoy
                        class="the-header__icon the-header__icon--lifebuoy"
                    />
                </template>
            </BaseDropdown>
            <!--<BaseDropdown
                button-variant="primary"
                button-type="circle"
                button-class="the-header__btn"
                tooltip-text="Уведомления"
            >
                <template #button-content>
                    <IconBell class="the-header__icon" />
                </template>
            </BaseDropdown>-->
            <BaseButton
                class="the-header__btn the-header__btn--ghost"
                variant="invisible"
                type="circle"
                v-tooltip.bottom="$t('orders.exit')"
                @click="exitModal.show()"
            >
                <IconArrowRightFromBracket class="the-header__icon" />
            </BaseButton>
            <TheHeaderExitModal ref="exitModal" />
        </template>
    </BaseHeaderTemplate>
</template>

<style lang="scss" scoped>
.the-header {
    &__collapse-btn {
        // Бургер: белый фон + тёмные чёрточки. Без видимой рамки (border того же цвета что фон)
        --button-border-color: var(--button-color);
        --button-border-color-hover: var(--button-color);
        --button-width: var(--header-height);
        --button-height: var(--header-height);
        --button-border-radius: 0;

        flex-shrink: 0;

        margin-right: auto;

        ::v-deep(svg) {
            color: var(--c-neutral-70);
        }
    }

    &__fast-create-btn {
        margin-right: 10px;
        margin-left: 10px;
    }

    &__icon {
        pointer-events: none;

        &--lifebuoy {
            width: 20px !important;
            height: auto;
        }
    }

    &__btn {
        flex-shrink: 0;

        &:active {
            svg {
                color: var(--c-green-10) !important;
            }
        }
    }

    // Ghost-кнопки (Спасательный круг, Выход) — без teal фона, тёмная иконка
    &__btn--ghost {
        --button-color: transparent;
        --button-color-hover: rgba(15 22 36 / 6%);
        --button-color-active: rgba(15 22 36 / 10%);
        --button-border-color: transparent;
        --button-border-color-hover: transparent;
        --button-border-color-active: transparent;

        ::v-deep(svg) {
            color: var(--c-neutral-70);
        }

        &:hover ::v-deep(svg) {
            color: var(--c-text);
        }
    }
}
</style>
